// Nothing here ever touches scroll position (no scrollIntoView, no
// focus(), no hash/fragment navigation) — a tap must never move the
// page. This custom element pushes the selected variety's marker
// position to the map (cross-DOM-subtree, since the summary boxes
// and the map live in different parts of the tree) and toggles which
// pre-rendered detail-card-item is visible in the shared detail card.
//
// The card is layered over the map (desktop only), like the homepage
// hero's jar overlay — sitting on top of it visually. `right` is a
// plain CSS inset (both card and map anchor to the same containing
// block's right: 0, so a fixed CSS value already lines up with the
// map's actual right edge). `top` can't be a plain CSS value the same
// way: the card's bottom edge is anchored to .tayab-terroir__varieties'
// actual rendered bottom edge (the section's designated bottom
// boundary — deliberately NOT the map's bottom, which is left
// unchanged and allowed to extend past that boundary), and that
// depends on both the varieties list's live position (varies with the
// intro block's height, which itself varies with line-wrapping at
// narrower widths) and the card's own rendered height (varies with
// which variety's content is active) — neither of which a CSS
// percentage can express (same reason marker positioning below needs
// JS rather than a plain CSS %). It's computed from both elements'
// live getBoundingClientRect() every time selection/resize/image-load
// could change either one, same trigger points already used for the
// marker.
//
// Marker X is a plain CSS %, via --tayab-terroir-marker-x:
// .tayab-terroir__map has no horizontal crop at all now, so the box's
// width always equals the image's true full width, and % across it
// is already correct. Y is different: .tayab-terroir__map's height is
// capped at 1050px (crops the bottom only, if the image's natural
// height at the current width would exceed that) — so the box's own
// rendered height can be shorter than the image's true height, and a
// plain "% of this box" would land marker_y in the wrong place once
// that cap is in effect. getNaturalHeightPx() computes the image's
// true height at the box's current width from its own aspect ratio,
// and the marker's Y position is derived from that instead of the
// box's rendered height — correct whether or not the cap is active.
//
// Initial zoom level for tap-to-center (applyTapZoom()) — a moderate,
// noticeable "closer look" rather than jumping straight to max, which
// would feel jarring and leave no room to pinch in further. Clamped to
// each open's own zoomMaxScale regardless, so this is a ceiling in
// practice on tablet (max ~1.58x there) and a real mid-range zoom on
// mobile (max ~3.56x).
const TAYAB_TERROIR_TAP_ZOOM_SCALE = 2;

class TayabTerroirMap extends HTMLElement {
  connectedCallback() {
    this.stageEl = this.querySelector('.tayab-terroir__stage');
    this.mapEl = this.querySelector('.tayab-terroir__map');
    this.markerEl = this.querySelector('.tayab-terroir__marker');
    this.glowEl = this.querySelector('.tayab-terroir__glow');
    this.varietiesEl = this.querySelector('.tayab-terroir__varieties');
    this.wrapEl = this.querySelector('.tayab-terroir__varieties-wrap');
    this.cardEl = this.querySelector('.tayab-terroir__detail-card');
    this.mapImgEl = this.mapEl ? this.mapEl.querySelector('img') : null;
    this.prevArrowEl = this.querySelector('.tayab-terroir__varieties-arrow--prev');
    this.nextArrowEl = this.querySelector('.tayab-terroir__varieties-arrow--next');
    this.zoomTriggerEl = this.querySelector('.tayab-terroir__map-zoom-trigger');
    this.zoomEl = this.querySelector('.tayab-terroir__zoom');
    this.zoomCloseEl = this.zoomEl ? this.zoomEl.querySelector('.tayab-terroir__zoom-close') : null;
    this.zoomFrameEl = this.zoomEl ? this.zoomEl.querySelector('.tayab-terroir__zoom-frame') : null;
    this.zoomStageEl = this.zoomEl ? this.zoomEl.querySelector('.tayab-terroir__zoom-stage') : null;
    this.zoomImgEl = this.zoomEl ? this.zoomEl.querySelector('.tayab-terroir__zoom-image') : null;
    this.zoomPointers = new Map();
    this.zoomScale = 1;
    this.zoomPanX = 0;
    this.zoomPanY = 0;
    this.zoomMaxScale = 1;
    this.zoomBaseWidth = 0;
    this.zoomBaseHeight = 0;
    this.zoomTapPoint = null;

    // Moved to <body> so its position: fixed is relative to the true
    // viewport, not to this custom element's own box. This element
    // carries the sitewide scroll-reveal classes (scroll-trigger
    // animate--slide-in, from settings.animations_reveal_on_scroll),
    // which apply a CSS transform — any transform on an ancestor turns
    // position: fixed descendants into a new containing block relative
    // to THAT ancestor instead of the viewport. Confirmed live: before
    // this move, the overlay rendered 1170px tall inside an 844px
    // viewport, offset -20px, tracking this section's own scrolled box
    // instead of the screen (its close button ended up partly above
    // the visible viewport). Same reason Dawn's own ModalDialog
    // (global.js) relocates itself on connect.
    //
    // An earlier version of this fix also copied --color-background
    // onto the relocated element, to patch the frame's background
    // inheriting from a .color-{scheme} class it's no longer a
    // descendant of. That's now moot: .tayab-terroir__zoom-frame uses
    // the shared hardcoded parchment recipe (tayab-terroir-map.css),
    // the same brand-constant colors as tayab-custom.css's body
    // background and tayab-product-terroir.css's hero — not tied to
    // the section's scheme variable at all, matching how every other
    // instance of this recipe works.
    if (this.zoomEl && this.zoomEl.parentElement !== document.body) {
      document.body.appendChild(this.zoomEl);
    }

    this.addEventListener('change', (event) => {
      const input = event.target;
      if (input.type !== 'radio') return;
      this.selectVariety(input);
    });

    const checked = this.querySelector('.tayab-terroir__variety-radio:checked');
    if (checked) this.selectVariety(checked);
    this.defaultCheckedRadio = checked;

    // Theme editor: selecting a variety block in the sidebar (or editing
    // one of its settings, e.g. dragging the marker_x/marker_y sliders)
    // re-renders this section fresh from Liquid, which always checks
    // whichever radio is the section's configured default — not
    // whichever variety was being edited. Without this, the marker never
    // visibly moves while editing a non-default variety's coordinates:
    // you're always looking at the default variety's marker regardless
    // of which block's settings panel is open. shopify:block:select
    // fires (and re-fires after each such reload) with event.target set
    // to the block's own element (the label carrying block.shopify_attributes),
    // matching the pattern theme-editor.js already uses for slideshow
    // slides — switch the checked radio to that block's own input so the
    // preview follows whichever variety is actually being edited.
    this.addEventListener('shopify:block:select', (event) => {
      const radio = event.target.querySelector('.tayab-terroir__variety-radio');
      if (!radio) return;
      radio.checked = true;
      this.selectVariety(radio);
    });

    this.addEventListener('shopify:block:deselect', () => {
      if (!this.defaultCheckedRadio) return;
      this.defaultCheckedRadio.checked = true;
      this.selectVariety(this.defaultCheckedRadio);
    });

    // naturalWidth/naturalHeight are 0 until the image has actually
    // loaded — re-run once it has, in case connectedCallback ran first.
    if (this.mapImgEl && !this.mapImgEl.complete) {
      this.mapImgEl.addEventListener('load', () => this.positionAll(), { once: true });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.positionAll());
    }

    let resizeScheduled = false;
    window.addEventListener('resize', () => {
      if (resizeScheduled) return;
      resizeScheduled = true;
      requestAnimationFrame(() => {
        resizeScheduled = false;
        this.positionAll();
        this.updateArrowState();
        // Re-fit rather than try to preserve the relative zoom/pan
        // across a dimension change (e.g. orientation rotation) — a
        // rare edge case, and re-centering is simpler and safer than
        // math that could leave the view stranded outside its new
        // clamped bounds.
        if (this.zoomEl && this.zoomEl.classList.contains('is-open')) this.resetZoomTransform();
      });
    });

    // Shared scroll-arrow controls — same buttons/methods handle both
    // mobile/tablet's horizontal row and desktop's vertical column,
    // branching on matchMedia inside scrollVarieties()/updateArrowState().
    if (this.prevArrowEl && this.nextArrowEl && this.varietiesEl) {
      this.prevArrowEl.addEventListener('click', () => this.scrollVarieties(-1));
      this.nextArrowEl.addEventListener('click', () => this.scrollVarieties(1));
      this.varietiesEl.addEventListener('scroll', () => this.updateArrowState(), { passive: true });
      this.updateArrowState();
    }

    // Mobile/tablet-only tap-to-zoom (the trigger button itself is
    // display: none at 990px+, but guard here too rather than relying
    // solely on CSS to keep this JS honest about being conditional).
    // Doesn't touch selectVariety()/positionAll() — it only reads the
    // currently-checked radio's dataset once, at the moment it opens,
    // the same source positionMarker() reads.
    //
    // zoomScrollY is captured on pointerdown, not inside openZoom()'s
    // own click handler — pointerdown fires before the browser's native
    // click-handling on the trigger <button>, which measured on a real
    // page as already having nudged window.scrollY by the time a click
    // listener runs (a native focus-follows-click side effect, not
    // caused by any of this file's own code). Capturing here instead
    // gets the genuine pre-interaction position closeZoom() should
    // restore, not an already-drifted one.
    if (this.zoomTriggerEl && this.zoomEl && this.zoomCloseEl) {
      this.zoomTriggerEl.addEventListener('pointerdown', () => {
        this.zoomScrollY = window.scrollY;
      });
      // event.detail (click count) is 0 for a keyboard-activated click
      // (Enter/Space on the focused button) and >= 1 for a real
      // pointer-driven one — clientX/clientY are meaningless for the
      // former (always 0,0), so this only captures a tap point when
      // there's a genuine tapped position to center on.
      this.zoomTriggerEl.addEventListener('click', (event) => {
        this.zoomTapPoint = event.detail !== 0 ? this.computeTapFraction(event.clientX, event.clientY) : null;
        this.openZoom();
      });
      this.zoomCloseEl.addEventListener('click', () => this.closeZoom());
      this.zoomEl.addEventListener('click', (event) => {
        if (event.target === this.zoomEl) this.closeZoom();
      });
      this.zoomEl.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') this.closeZoom();
      });
    }

    // Pinch-to-zoom / drag-to-pan on the open overlay, Pointer Events
    // (not Touch Events) so mouse/touch/pen all go through one code
    // path. touch-action: none on the frame (CSS) hands the gesture
    // entirely to this code instead of letting the browser's own
    // native page-zoom/pan compete with it.
    if (this.zoomFrameEl) {
      this.zoomFrameEl.addEventListener('pointerdown', (event) => this.onZoomPointerDown(event));
      this.zoomFrameEl.addEventListener('pointermove', (event) => this.onZoomPointerMove(event));
      this.zoomFrameEl.addEventListener('pointerup', (event) => this.onZoomPointerUp(event));
      this.zoomFrameEl.addEventListener('pointercancel', (event) => this.onZoomPointerUp(event));
    }
  }

  // Scrolls by roughly one row's height (desktop, min-width: 990px)
  // or one button's width (mobile/tablet) — measured live from the
  // first rendered box rather than a fixed guess, since its size
  // varies by variety (icon + however long its common/Latin names
  // are). Adds the list's own gap so consecutive scrolls land just
  // past the next item's start rather than mid-item.
  scrollVarieties(direction) {
    const firstBox = this.varietiesEl.querySelector('.tayab-terroir__variety-box');
    const isVertical = window.matchMedia('(min-width: 990px)').matches;
    if (isVertical) {
      const gapPx = parseFloat(getComputedStyle(this.varietiesEl).rowGap) || 0;
      const stepPx = firstBox ? firstBox.getBoundingClientRect().height + gapPx : 74;
      this.varietiesEl.scrollBy({ top: direction * stepPx, behavior: 'smooth' });
    } else {
      const gapPx = parseFloat(getComputedStyle(this.varietiesEl).columnGap) || 0;
      const stepPx = firstBox ? firstBox.getBoundingClientRect().width + gapPx : 160;
      this.varietiesEl.scrollBy({ left: direction * stepPx, behavior: 'smooth' });
    }
  }

  // Disables (dims, doesn't hide) whichever arrow has nothing left to
  // scroll toward — hiding would reflow the available space every
  // time you hit an edge, since the list shares a flex parent with
  // both arrows on both axes; disabling keeps that space perfectly
  // stable. Also keeps each arrow's aria-label matching its actual
  // direction, since the same two buttons mean up/down at desktop but
  // left/right at mobile/tablet.
  updateArrowState() {
    if (!this.prevArrowEl || !this.nextArrowEl || !this.varietiesEl) return;
    const isVertical = window.matchMedia('(min-width: 990px)').matches;
    if (isVertical) {
      const { scrollTop, scrollHeight, clientHeight } = this.varietiesEl;
      this.prevArrowEl.disabled = scrollTop <= 1;
      this.nextArrowEl.disabled = scrollTop + clientHeight >= scrollHeight - 1;
      this.prevArrowEl.setAttribute('aria-label', 'Scroll varieties up');
      this.nextArrowEl.setAttribute('aria-label', 'Scroll varieties down');
    } else {
      const { scrollLeft, scrollWidth, clientWidth } = this.varietiesEl;
      this.prevArrowEl.disabled = scrollLeft <= 1;
      this.nextArrowEl.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
      this.prevArrowEl.setAttribute('aria-label', 'Scroll varieties left');
      this.nextArrowEl.setAttribute('aria-label', 'Scroll varieties right');
    }
  }

  // The image's true rendered height at the map box's current width,
  // from its own natural aspect ratio — independent of whatever the
  // box's own (possibly bottom-capped) height currently is. Falls
  // back to the box's own current ratio if there's no image yet
  // (e.g. the no-image placeholder state), so nothing divides by zero.
  getNaturalHeightPx(mapRect) {
    const img = this.mapImgEl;
    if (img && img.naturalWidth && img.naturalHeight) {
      return mapRect.width / (img.naturalWidth / img.naturalHeight);
    }
    return mapRect.height;
  }

  // Opens the mobile/tablet zoom lightbox. Reads the same checked
  // radio's data-marker-x/-y that positionMarker() reads, but writes
  // them as plain CSS percentages instead of a JS-computed pixel value
  // — .tayab-terroir__zoom-stage (which the marker/glow percentages
  // resolve against) is sized by resetZoomTransform() below to exactly
  // the image's own rendered box, uncropped, so a plain % is already
  // correct here and doesn't need the getNaturalHeightPx() conversion
  // positionMarker() requires for the in-page map. trapFocus/
  // removeTrapFocus and .overflow-hidden are the same sitewide
  // utilities Dawn's own product-media-modal uses (global.js, loaded
  // before this script), reused here rather than reimplemented.
  openZoom() {
    if (!this.zoomEl) return;
    const checked = this.querySelector('.tayab-terroir__variety-radio:checked');
    if (checked) {
      this.zoomEl.style.setProperty('--tayab-terroir-zoom-marker-x', parseFloat(checked.dataset.markerX) + '%');
      this.zoomEl.style.setProperty('--tayab-terroir-zoom-marker-y', parseFloat(checked.dataset.markerY) + '%');
    }
    // zoomScrollY is normally already set by the pointerdown listener
    // above (before this click-triggered method even runs) — this is
    // only a fallback for any other future caller of openZoom() that
    // didn't come through that listener.
    if (typeof this.zoomScrollY !== 'number') this.zoomScrollY = window.scrollY;
    this.zoomEl.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    // Always starts back at fit-to-screen/no pan, not wherever a
    // previous session left off — resetZoomTransform() also needs the
    // frame's now-current layout (classList.add above already flushed
    // it synchronously), so it belongs after that, not before.
    this.resetZoomTransform();
    // Then, if this open came from a real tap (not a keyboard
    // activation), overrides that plain fit with a zoom centered on
    // exactly the tapped spot — kept as a separate step rather than
    // folded into resetZoomTransform() itself, since that method is
    // also called on resize while already open, where re-applying the
    // original tap point would fight whatever the user has since
    // pinched/panned to themselves.
    this.applyTapZoom();
    this.updateZoomLoadedState();
    // Focusing the close button ourselves first, with preventScroll,
    // avoids a real measured bug: trapFocus() below does its own bare
    // elementToFocus.focus() (no options — it's a shared sitewide
    // function, not something this section can change), and calling
    // that on an element that just became visible this same tick
    // scrolled the page ~450px on a real page (confirmed by bisecting
    // step by step — not a plain "off-screen fixed element" issue,
    // since the close button is already within the fixed overlay's
    // viewport-covering box either way). Pre-focusing here first means
    // trapFocus's own focus() call lands on an element that's already
    // the activeElement, which browsers treat as a no-op with no
    // scroll side effect.
    this.zoomCloseEl.focus({ preventScroll: true });
    trapFocus(this.zoomEl, this.zoomCloseEl);
  }

  // Toggles .is-loaded (CSS fades the stage in and hides the spinner —
  // see tayab-terroir-map.css) once the zoom image has actually
  // finished loading, rather than assuming eager loading means it
  // already has. Confirmed live with a throttled network: without
  // this, a slow connection shows the frame's plain background with
  // nothing on it — no visibly broken image, no obvious sign anything
  // is happening, just what reads as "the map didn't load" — for
  // however long the request takes. Also handles 'error' (still
  // reveals the stage rather than spinning forever if the request
  // genuinely fails; the browser's own broken-image state is at least
  // legible, an infinite spinner isn't).
  updateZoomLoadedState() {
    if (!this.zoomEl || !this.zoomImgEl) return;
    if (this.zoomImgEl.complete && this.zoomImgEl.naturalWidth) {
      this.zoomEl.classList.add('is-loaded');
      return;
    }
    this.zoomEl.classList.remove('is-loaded');
    const onSettle = () => this.zoomEl.classList.add('is-loaded');
    this.zoomImgEl.addEventListener('load', onSettle, { once: true });
    this.zoomImgEl.addEventListener('error', onSettle, { once: true });
  }

  // Computes the image's "cover fit" box (base zoom = 1) centered in the
  // frame, and resets scale/pan to that fit — always run on open, and on
  // resize while open, so the view never remembers a previous session's
  // zoom/pan.
  //
  // Math.max (cover), not Math.min (contain): confirmed live on a real
  // Android phone that contain fit — sized to show the WHOLE image with
  // no cropping — left large blank parchment bands above and below the
  // map, filling only ~34-37% of the frame's height at typical phone
  // widths. The source image is landscape (1218x1024, ~1.19:1) while a
  // phone frame is tall/narrow, so contain fit is always constrained by
  // width there, wasting most of the vertical space. Cover fit fills
  // whichever axis is LESS constrained completely and crops the other
  // (recoverable by panning — clampZoomPan() below already allows
  // panning on an overflowing axis even at scale 1, no change needed
  // there). zoomMaxScale below still converges on the same native-
  // resolution ceiling either way (zoomBaseWidth * zoomMaxScale ==
  // naturalWidth under both fits) — cover fit just starts closer to it,
  // so there's proportionally less headroom left to pinch further,
  // which is expected, not a bug.
  //
  // naturalWidth/naturalHeight come from data-natural-width/-height on
  // the frame (Shopify's image.width/height in the Liquid, the
  // original uploaded file's true pixel dimensions) — not
  // this.zoomImgEl.naturalWidth. Measured live that the <img>'s own
  // naturalWidth reflects whichever responsive srcset candidate the
  // browser actually decided to fetch for the current render size
  // (390 on a 390px-wide phone, nowhere near the real ~1218px source),
  // not the source asset's true resolution — using it would have made
  // the max-zoom ceiling below wildly too conservative, and wrongly so
  // depending on viewport width. The Liquid-sourced values are also
  // correct for whatever image a merchant uploads, not just today's.
  resetZoomTransform() {
    if (!this.zoomFrameEl || !this.zoomStageEl || !this.zoomImgEl) return;

    const naturalWidth = parseFloat(this.zoomFrameEl.dataset.naturalWidth) || 0;
    const naturalHeight = parseFloat(this.zoomFrameEl.dataset.naturalHeight) || 0;
    if (!naturalWidth || !naturalHeight) return;

    const frameRect = this.zoomFrameEl.getBoundingClientRect();
    const coverScale = Math.max(frameRect.width / naturalWidth, frameRect.height / naturalHeight);

    this.zoomBaseWidth = naturalWidth * coverScale;
    this.zoomBaseHeight = naturalHeight * coverScale;

    // Sensible ceiling: cap the displayed CSS-pixel size at the image's
    // real native resolution, not accounting for devicePixelRatio
    // precisely — a strict per-device-pixel ceiling would allow almost
    // no zoom at all on a high-DPR phone (~1.2x), defeating the point
    // of the feature; this is the same simplification most web image
    // viewers make. Math.max(…, 1) guards the case where the cover-fit
    // size is already ≥ native size (common on phone widths now that
    // cover fit itself already uses up most of the resolution headroom).
    this.zoomMaxScale = Math.max(1, naturalWidth / this.zoomBaseWidth);

    this.zoomScale = 1;
    this.zoomPanX = 0;
    this.zoomPanY = 0;
    this.zoomPointers.clear();
    this.zoomDragStart = null;
    this.settleZoomLayout();
  }

  // Commits the current zoomScale/zoomPanX/zoomPanY as a plain, non-
  // transformed layout box — real width/height/left/top, not a CSS
  // transform — for whenever the view is at rest: open, reset, and the
  // end of every gesture. Never called while a gesture is actively live
  // (see unsettleZoomLayout() below).
  //
  // Load-bearing, not a style preference: a CSS transform: scale() on
  // this element — however it's applied, WAAPI or a plain style write,
  // with or without will-change, all confirmed live — makes the browser
  // GPU-composite-stretch its ALREADY-rasterized background instead of
  // repainting it at the new size. That's invisible on the map image
  // itself (photographic/line-art content tolerates a 2-3x stretch as
  // mild softening), but turns the fine paper-grain texture visibly
  // blocky, confirmed on a real Android phone. Isolated the cause with a
  // controlled A/B: identical SVG, identical scale factors, layout-
  // resized boxes vs. transform-scaled boxes — layout resizing rendered
  // identically crisp at every size tested (this is genuinely just a
  // repaint), while transform scaling grew measurably coarser in
  // lockstep with the scale factor, regardless of whether the grain was
  // a CSS background, an SVG <img>, or a pre-baked PNG <img> (measured
  // via average pixel-transition run length: ~1.27px unscaled/homepage,
  // growing to ~4.4px at 3.56x under transform, vs. ~1.2-1.3px at every
  // scale once genuinely relaid-out instead) — the compositor treats
  // all three resource types the same way once a transform is involved,
  // so switching resource type alone (e.g. to a real bitmap) doesn't
  // fix it; only avoiding the transform for the resting view does.
  // transform-based updates are kept ONLY for the live gesture itself
  // (applyZoomTransform(), still called from onZoomPointerMove) — the
  // trade-off there (transient softening while the view is already
  // moving) is worth the alternative, since relayout on every
  // pointermove is expensive enough to reintroduce jank, per
  // applyZoomTransform()'s own comment on why it avoids layout-
  // triggering properties for that specific case. will-change: transform
  // is likewise only asserted while a gesture is actually live
  // (unsettleZoomLayout()) rather than permanently in CSS, since there's
  // nothing to hint the browser to optimize for once transform: none.
  settleZoomLayout() {
    if (!this.zoomStageEl || !this.zoomFrameEl) return;
    if (this.zoomCurrentAnimation) {
      this.zoomCurrentAnimation.cancel();
      this.zoomCurrentAnimation = null;
    }
    const frameRect = this.zoomFrameEl.getBoundingClientRect();
    const width = this.zoomBaseWidth * this.zoomScale;
    const height = this.zoomBaseHeight * this.zoomScale;
    this.zoomStageEl.style.position = 'absolute';
    this.zoomStageEl.style.width = width + 'px';
    this.zoomStageEl.style.height = height + 'px';
    this.zoomStageEl.style.left = (frameRect.width - width) / 2 + this.zoomPanX + 'px';
    this.zoomStageEl.style.top = (frameRect.height - height) / 2 + this.zoomPanY + 'px';
    this.zoomStageEl.style.transform = 'none';
    this.zoomStageEl.style.willChange = 'auto';
  }

  // Reverses settleZoomLayout(), just before a new gesture begins:
  // resets the stage's layout box back to the fixed zoomBaseWidth/
  // zoomBaseHeight baseline (undoing whatever size settleZoomLayout()
  // last committed) and re-expresses the current scale/pan as a
  // transform instead, via applyZoomTransform() — so that method's
  // math, always relative to that fixed baseline, stays valid for the
  // live pointermove updates about to start. Called from
  // onZoomPointerDown, once per gesture (when the pointer count goes
  // from 0 to 1), not on every pointerdown.
  unsettleZoomLayout() {
    if (!this.zoomStageEl || !this.zoomFrameEl) return;
    const frameRect = this.zoomFrameEl.getBoundingClientRect();
    this.zoomStageEl.style.willChange = 'transform';
    this.zoomStageEl.style.width = this.zoomBaseWidth + 'px';
    this.zoomStageEl.style.height = this.zoomBaseHeight + 'px';
    this.zoomStageEl.style.left = (frameRect.width - this.zoomBaseWidth) / 2 + 'px';
    this.zoomStageEl.style.top = (frameRect.height - this.zoomBaseHeight) / 2 + 'px';
    this.applyZoomTransform();
  }

  // Converts a real screen position into a 0-1 fraction of the map
  // box, used to open the zoom overlay centered on exactly where the
  // user tapped. The trigger's own box is used as the reference frame
  // rather than the image's — .tayab-terroir__map-zoom-trigger is
  // position: absolute; inset: 0 within .tayab-terroir__map, and the
  // image itself is never cropped at mobile/tablet (only desktop's
  // in-page map can crop, and this trigger doesn't exist there), so
  // the trigger's box and the image's own rendered box are identical
  // at every width this runs at. Clamped to [0,1] so a tap right at
  // the very edge doesn't produce a fraction that would, after the
  // scale/pan math below, ask to pan past where clampZoomPan() would
  // put it anyway.
  computeTapFraction(clientX, clientY) {
    const rect = this.zoomTriggerEl.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  }

  // Overrides the plain fit-to-screen resetZoomTransform() just set up
  // with a zoom centered on zoomTapPoint (captured by the trigger's
  // click handler), if there is one — a keyboard-activated open leaves
  // it null and this is a no-op, same as before this feature existed.
  //
  // Math: the stage starts centered in the frame (resetZoomTransform()
  // above), and transform-origin: center center means scale is applied
  // around that same center point. A point at fraction (fx, fy) of the
  // stage sits at local offset d = (fx - 0.5) * baseSize from that
  // center, in the stage's own unscaled coordinate space. After
  // transform: translate(pan) scale(s), that point paints at
  // frameCenter + s*d + pan (same relation TAYAB_TERROIR_ZOOM_ANCHOR
  // — see onZoomPointerMove's pinch-anchoring comment — is built on).
  // Landing it exactly on the frame's own center (pan's whole job)
  // means solving frameCenter + s*d + pan = frameCenter, i.e.
  // pan = -s*d.
  applyTapZoom() {
    if (!this.zoomTapPoint || !this.zoomBaseWidth || !this.zoomBaseHeight) return;
    const targetScale = Math.min(this.zoomMaxScale, Math.max(1, TAYAB_TERROIR_TAP_ZOOM_SCALE));
    const localX = (this.zoomTapPoint.x - 0.5) * this.zoomBaseWidth;
    const localY = (this.zoomTapPoint.y - 0.5) * this.zoomBaseHeight;
    this.zoomScale = targetScale;
    this.zoomPanX = -targetScale * localX;
    this.zoomPanY = -targetScale * localY;
    this.clampZoomPan();
    // settleZoomLayout(), not applyZoomTransform() — a tap-opened view is
    // still an at-rest state (see that method's comment), and the whole
    // reason this exists is that a real tap always lands here first.
    this.settleZoomLayout();
  }

  // Applies the pan/zoom transform via the Web Animations API instead
  // of a plain style.transform write, and does so synchronously (no
  // requestAnimationFrame deferral) — both are load-bearing, not a
  // style preference. Bisected a real, reproducible issue: mutating
  // this element's transform mid-drag caused the browser to silently
  // stop dispatching further pointermove events for the rest of that
  // gesture (confirmed with a minimal, app-independent repro page —
  // not specific to anything else in this file). Of every combination
  // tested, only an immediate (non-deferred) WAAPI update avoided it:
  // a plain style.transform write broke it whether synchronous or
  // rAF-deferred; a *deferred* WAAPI update broke it too (so rAF
  // deferral itself was implicated, not the write mechanism alone).
  // Switching to layout-triggering properties (left/top) also avoided
  // it but gives up GPU-composited panning, which this keeps.
  // Canceling any in-flight animation first keeps rapid updates (a
  // fast drag/pinch) from stacking up competing animations.
  //
  // The keyframe list is the SAME value twice, not once — this fixed a
  // second real, reproducible bug found later: a single keyframe makes
  // WAAPI implicitly start the animation from the element's *current*
  // computed style, and since cancel() just above strips the previous
  // animation's effect immediately, that current style is this
  // element's un-animated base transform (none/identity — there's no
  // CSS transform rule on .tayab-terroir__zoom-stage at all). Confirmed
  // by polling getComputedStyle via requestAnimationFrame right after a
  // call: frame 0 rendered matrix(1,0,0,1,0,0) — full identity, i.e.
  // the plain unzoomed view — before the target value took over on the
  // next frame, on every single call. During a real gesture (many
  // calls in quick succession) that's a repeated snap-to-default-then-
  // jump-back, exactly the "jumpy/unstable movement" reported. Two
  // identical keyframes give the animation nothing to interpolate
  // from, which the same polling test confirms removes the flash
  // entirely — every sampled frame already shows the target value.
  applyZoomTransform() {
    if (!this.zoomStageEl) return;
    const transformValue = `translate(${this.zoomPanX}px, ${this.zoomPanY}px) scale(${this.zoomScale})`;
    if (this.zoomCurrentAnimation) this.zoomCurrentAnimation.cancel();
    this.zoomCurrentAnimation = this.zoomStageEl.animate(
      [{ transform: transformValue }, { transform: transformValue }],
      { duration: 1, fill: 'forwards' }
    );
  }

  // Pan range at the current scale — the image starts centered
  // (resetZoomTransform() above), so any overflow beyond the frame is
  // symmetric: half of it can be panned to on either side. At scale 1
  // this always clamps to exactly 0 either way, so there's nothing
  // extra needed to keep an unzoomed image from being draggable.
  clampZoomPan() {
    if (!this.zoomFrameEl) return;
    const maxPanX = Math.max(0, (this.zoomBaseWidth * this.zoomScale - this.zoomFrameEl.clientWidth) / 2);
    const maxPanY = Math.max(0, (this.zoomBaseHeight * this.zoomScale - this.zoomFrameEl.clientHeight) / 2);
    this.zoomPanX = Math.min(maxPanX, Math.max(-maxPanX, this.zoomPanX));
    this.zoomPanY = Math.min(maxPanY, Math.max(-maxPanY, this.zoomPanY));
  }

  onZoomPointerDown(event) {
    // Once per gesture (0 -> 1 pointers, before a pinch's second pointer
    // can arrive), not once per pointer — see unsettleZoomLayout()'s
    // comment for why this needs to happen before any live transform
    // update in this gesture.
    if (this.zoomPointers.size === 0) this.unsettleZoomLayout();
    this.zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.zoomPointers.size === 2) {
      const pts = Array.from(this.zoomPointers.values());
      this.zoomPinchStartDistance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      this.zoomPinchStartScale = this.zoomScale;
      this.zoomPinchMidpoint = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
    } else if (this.zoomPointers.size === 1) {
      const p = this.zoomPointers.values().next().value;
      this.zoomDragStart = { x: p.x, y: p.y, panX: this.zoomPanX, panY: this.zoomPanY };
    }
  }

  // Two active pointers = pinch (scale from the change in distance
  // between them, anchored so the pinch midpoint stays visually fixed
  // on screen — see the derivation below); one = drag-to-pan.
  //
  // Anchoring math: transform-origin: center center means a point at
  // local offset d from the stage's own center (== the frame's center,
  // since the stage starts centered) paints at frame-relative position
  // frameCenter + scale*d + pan. Solving for the pan that keeps a
  // fixed frame-relative point M under the fingers as scale goes from
  // s0 to s1: pan1 = Mrel*(1 - s1/s0) + pan0*(s1/s0), where
  // Mrel = M - frameCenter. That's exactly what midX/midY and ratio
  // below compute.
  onZoomPointerMove(event) {
    if (!this.zoomPointers.has(event.pointerId)) return;
    this.zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.zoomPointers.size === 2) {
      event.preventDefault();
      const pts = Array.from(this.zoomPointers.values());
      const distance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const rawScale = this.zoomPinchStartScale * (distance / this.zoomPinchStartDistance);
      const newScale = Math.min(this.zoomMaxScale, Math.max(1, rawScale));

      const frameRect = this.zoomFrameEl.getBoundingClientRect();
      const midX = this.zoomPinchMidpoint.x - frameRect.left - frameRect.width / 2;
      const midY = this.zoomPinchMidpoint.y - frameRect.top - frameRect.height / 2;
      const ratio = newScale / this.zoomScale;
      this.zoomPanX = midX * (1 - ratio) + this.zoomPanX * ratio;
      this.zoomPanY = midY * (1 - ratio) + this.zoomPanY * ratio;
      this.zoomScale = newScale;
      this.clampZoomPan();
      this.applyZoomTransform();
    } else if (this.zoomPointers.size === 1 && this.zoomDragStart) {
      event.preventDefault();
      const p = this.zoomPointers.values().next().value;
      this.zoomPanX = this.zoomDragStart.panX + (p.x - this.zoomDragStart.x);
      this.zoomPanY = this.zoomDragStart.panY + (p.y - this.zoomDragStart.y);
      this.clampZoomPan();
      this.applyZoomTransform();
    }
  }

  onZoomPointerUp(event) {
    this.zoomPointers.delete(event.pointerId);
    if (this.zoomPointers.size === 1) {
      // Dropped from two pointers to one (end of a pinch) — re-anchor
      // the drag-start reference to the remaining finger's current
      // position, so it continues panning from here instead of
      // jumping to account for the finger that was just lifted.
      const p = this.zoomPointers.values().next().value;
      this.zoomDragStart = { x: p.x, y: p.y, panX: this.zoomPanX, panY: this.zoomPanY };
    } else {
      this.zoomDragStart = null;
      // Gesture fully ended (last pointer lifted) — commit back to a
      // crisp, non-transformed layout box. See settleZoomLayout()'s
      // comment.
      if (this.zoomPointers.size === 0) this.settleZoomLayout();
    }
  }

  closeZoom() {
    if (!this.zoomEl) return;
    this.zoomEl.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    // In case a gesture was mid-flight when this closed (e.g. Escape
    // pressed mid-drag) — resetZoomTransform() on the next open would
    // clear these anyway, but not leaving stale pointer state sitting
    // around in the meantime is cheap and one less thing to reason
    // about if openZoom() is ever called some other way.
    this.zoomPointers.clear();
    this.zoomDragStart = null;
    this.zoomTapPoint = null;
    // Not removeTrapFocus(this.zoomTriggerEl) — that calls a plain
    // .focus() with no options, and preventScroll: true here is belt
    // and suspenders, not the actual fix (see openZoom()'s comment on
    // zoomScrollY — this specific drift turned out to come from the
    // browser's native handling of the close button click itself, not
    // from either of this file's own focus() calls).
    removeTrapFocus();
    if (this.zoomTriggerEl) this.zoomTriggerEl.focus({ preventScroll: true });
    if (typeof this.zoomScrollY === 'number') {
      window.scrollTo(0, this.zoomScrollY);
    }
  }

  selectVariety(input) {
    this.querySelectorAll('.tayab-terroir__detail-card-item').forEach((item) => {
      item.classList.toggle('is-active', item.dataset.varietyId === input.value);
    });
    this.positionAll();
  }

  positionAll() {
    this.updateStageHeight();
    this.positionMarker();
    this.positionDetailCard();
  }

  // Same pattern as the homepage hero's ResizeObserver-driven
  // --tayab-hero-text-block-height (tayab-custom.css/image-banner.liquid)
  // — a fixed CSS height can't express "as tall as whichever is
  // taller: the map (position: absolute, varies independently with
  // merchant-configured map_width/map_scale/page_width) or the
  // flow-stacked intro+varieties column," so it's measured live here
  // instead, same trigger points already used for marker/card.
  // .tayab-terroir__detail-card is deliberately NOT included in this
  // comparison — positionDetailCard() always pins its bottom edge to
  // the varieties column's bottom, so it can never be the tallest
  // element; relying on that existing invariant instead of measuring
  // it separately here too.
  updateStageHeight() {
    if (!this.stageEl || !this.wrapEl || !this.mapEl) return;
    if (!window.matchMedia('(min-width: 990px)').matches) {
      this.style.removeProperty('--tayab-terroir-stage-height');
      return;
    }

    const stageRect = this.stageEl.getBoundingClientRect();
    const wrapBottomPx = this.wrapEl.getBoundingClientRect().bottom - stageRect.top;
    const mapBottomPx = this.mapEl.getBoundingClientRect().bottom - stageRect.top;

    this.style.setProperty('--tayab-terroir-stage-height', Math.max(wrapBottomPx, mapBottomPx) + 'px');
  }

  positionMarker() {
    if (!this.mapEl || !this.markerEl || !this.glowEl) return;

    const checked = this.querySelector('.tayab-terroir__variety-radio:checked');
    if (!checked) return;

    const markerXPercent = parseFloat(checked.dataset.markerX);
    const markerYPercent = parseFloat(checked.dataset.markerY);
    if (Number.isNaN(markerXPercent) || Number.isNaN(markerYPercent)) return;

    const mapRect = this.mapEl.getBoundingClientRect();
    const naturalHeightPx = this.getNaturalHeightPx(mapRect);
    const markerTopPx = (markerYPercent / 100) * naturalHeightPx;

    this.style.setProperty('--tayab-terroir-marker-x', markerXPercent + '%');
    this.markerEl.style.top = markerTopPx + 'px';
    this.glowEl.style.top = markerTopPx + 'px';
  }

  positionDetailCard() {
    if (!this.varietiesEl || !this.cardEl || !this.stageEl) return;
    if (!window.matchMedia('(min-width: 990px)').matches) {
      this.cardEl.style.top = '';
      return;
    }

    const stageRect = this.stageEl.getBoundingClientRect();
    const varietiesRect = this.varietiesEl.getBoundingClientRect();
    const cardRect = this.cardEl.getBoundingClientRect();
    const varietiesBottomPx = varietiesRect.bottom - stageRect.top;

    // Card's own bottom edge lands exactly on the varieties list's
    // bottom edge — no inset, a literal shared anchor point (that
    // list's bottom is the section's designated bottom boundary; the
    // map is deliberately left alone and extends past it). cardRect
    // .height is read live (not assumed) because it varies by which
    // variety's content is currently active. varietiesRect.bottom
    // already reflects the list's max-height cap (see .tayab-terroir
    // __varieties in the CSS) if it's ever scrolling with 5+
    // varieties, so this needs no separate handling for that case.
    this.cardEl.style.top = (varietiesBottomPx - cardRect.height) + 'px';
  }
}

if (!customElements.get('tayab-terroir-map')) {
  customElements.define('tayab-terroir-map', TayabTerroirMap);
}
